/**
 * Drill registry for Accounting Dashboards: what opens when a count row is clicked.
 *
 * Every row key maps to a descriptor. Every row drills through the bucket cache's drill
 * endpoint (`/accounting-dashboards/drill/{rowKey}`) except the three in
 * `UNCACHED_ROW_KEYS`, which no bucket produces and which stay placeholders. Titles read
 * as breadcrumbs ("Tile › Parent › Row") so rows that share a label under different
 * parents ("Inv Amt > $0") never collide; "›" is the separator because two approved
 * labels already contain " - ".
 */
import type { CountPanelConfig, CountRow } from '../../components/CountPanel';
import { acctDrillPath } from '../../services/accountingDashboards.service';
import { ACCT_COUNT_PANELS } from './accountingPanels';

export type DrillDescriptor =
  | { kind: 'placeholder' }
  | {
      kind: 'endpoint';
      path: string;
      /** Must be JSON-serializable; useDrillData keys its effect on the serialized descriptor. */
      params?: Record<string, unknown>;
    };

export interface DrillTarget {
  rowKey: string;
  /** Provenance — which tile the row belongs to. */
  panelKey: string;
  /** "Tile › Parent › Row"; the tile name alone for a header row. */
  title: string;
  /** The row's own label; the tile title for a header row. The card's TanTable title. */
  leafLabel: string;
  descriptor: DrillDescriptor;
}

const SEP = ' › ';

/** Rows no bucket produces (design doc §4.3/§4.6): V1 derives Warehouse Storage from the
 *  storage job and computes EDI live; the Approval header has no count in V1 either. */
export const UNCACHED_ROW_KEYS: ReadonlySet<string> = new Set([
  'acct-pinv-warehouse-storage', 'acct-pinv-edi', 'acct-approval-total',
]);

const descriptorFor = (rowKey: string): DrillDescriptor =>
  UNCACHED_ROW_KEYS.has(rowKey) ? { kind: 'placeholder' } : { kind: 'endpoint', path: acctDrillPath(rowKey) };

const targetsFor = (panel: CountPanelConfig): DrillTarget[] => {
  const out: DrillTarget[] = [];
  const push = (rowKey: string, title: string, leafLabel: string) => out.push({
    rowKey,
    panelKey: panel.key,
    title,
    leafLabel,
    descriptor: descriptorFor(rowKey),
  });

  panel.columns.forEach((col) => {
    // Assumes a headed column's header label equals the tile title (true for all nine
    // panels); a two-section panel would need the header label here — the unique-titles
    // test will say so.
    if (col.header) push(col.header.key, panel.title, panel.title);
    // Labels of the enclosing rows, indexed by indent depth, so a nested row's title
    // reads down the hierarchy. A header-style row is a depth-0 group label.
    const path: string[] = [];
    col.rows.forEach((row: CountRow) => {
      const depth = row.indent || 0;
      path.length = depth;
      push(row.key, [panel.title, ...path, row.label].join(SEP), row.label);
      path[depth] = row.label;
    });
  });
  (panel.footers || []).forEach((f) => push(f.key, [panel.title, f.label].join(SEP), f.label));
  return out;
};

export const ACCT_DRILL_TARGETS: DrillTarget[] = ACCT_COUNT_PANELS.flatMap(targetsFor);

const TARGET_BY_KEY = new Map(ACCT_DRILL_TARGETS.map((t) => [t.rowKey, t]));

export const drillTargetFor = (rowKey: string): DrillTarget | undefined => TARGET_BY_KEY.get(rowKey);
